import React from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { richText } from '../../../util/richText';
import { types as sdkTypes } from '../../../util/sdkLoader';
import { getItemTypeLabel } from '../../../util/productTypes';
import { userDisplayNameAsString } from '../../../util/data';

import { AvatarMedium, FavoriteButton, Modal, NamedLink } from '../../../components';
import InquiryForm from '../InquiryForm/InquiryForm';

import css from './ListingInfo.module.css';

// Listing page sections of the Phase 2 mockup (mockup-6-listing-page-mobile.png).

const { Money } = sdkTypes;

const MIN_LENGTH_FOR_LONG_WORDS = 20;

const enumLabel = (listingFields, key, value) => {
  const field = listingFields?.find(f => f.key === key);
  return field?.enumOptions?.find(o => `${o.option}` === `${value}`)?.label || null;
};

const searchParams = (category, itemType) =>
  itemType
    ? `?pub_productCategory=${encodeURIComponent(category)}&pub_productItemType=${encodeURIComponent(itemType)}`
    : `?pub_productCategory=${encodeURIComponent(category)}`;

const IconLocation = () => (
  <svg className={css.iconLocation} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 2a7 7 0 0 0-7 7c0 5.3 7 13 7 13s7-7.7 7-13a7 7 0 0 0-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z" />
  </svg>
);

const IconBox = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M21 8l-9-5-9 5v8l9 5 9-5V8z" />
    <path d="M3 8l9 5 9-5M12 13v8M7.5 5.5l9 5" />
  </svg>
);

const IconCard = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <rect x="2.5" y="5" width="19" height="14" rx="2" />
    <path d="M2.5 10h19M6 15h1.5M9 15h1.5" />
  </svg>
);

const IconChat = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 3.5c-4.97 0-9 3.36-9 7.5 0 2.1 1.04 4 2.72 5.36L5 20.5l4.2-2.1c.9.23 1.84.35 2.8.35 4.97 0 9-3.36 9-7.5s-4.03-7.25-9-7.25z" />
    <path d="M8.5 11h.01M12 11h.01M15.5 11h.01" />
  </svg>
);

const IconSparkle = () => (
  <svg className={css.iconSparkle} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 3l1.6 4.4L15 9l-4.4 1.6L9 15l-1.6-4.4L3 9l4.4-1.6L9 3zM17.5 13l.9 2.6 2.6.9-2.6.9-.9 2.6-.9-2.6-2.6-.9 2.6-.9.9-2.6z" />
  </svg>
);

const IconChevron = () => (
  <svg className={css.iconChevron} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M9 5l7 7-7 7" />
  </svg>
);

const Breadcrumb = props => {
  const { category, itemType, className } = props;
  if (!category) {
    return null;
  }
  return (
    <span className={className}>
      <NamedLink name="SearchPage" to={{ search: searchParams(category) }}>
        {category}
      </NamedLink>
      {itemType ? (
        <>
          <span className={css.crumbSeparator} aria-hidden="true">
            ›
          </span>
          <NamedLink name="SearchPage" to={{ search: searchParams(category, itemType) }}>
            {getItemTypeLabel(itemType)}
          </NamedLink>
        </>
      ) : null}
    </span>
  );
};

const deliveryMessageId = (pickupEnabled, shippingEnabled) =>
  pickupEnabled && shippingEnabled
    ? 'ListingPage.deliveryBoth'
    : pickupEnabled
    ? 'ListingPage.deliveryCollection'
    : shippingEnabled
    ? 'ListingPage.deliveryShipping'
    : null;

/**
 * Breadcrumb, title, heart, price (+ struck-through price before the latest drop) and
 * county / delivery line. Phones and tablets only: the desktop order panel shows the same.
 */
export const ListingHeader = props => {
  const { listing, title, isOwnListing, intl, marketplaceCurrency, children } = props;
  const { price, publicData = {} } = listing.attributes;
  const { productCategory, productItemType, locationCounty, pickupEnabled, shippingEnabled } =
    publicData;

  const showPrice = price && price.currency === marketplaceCurrency;
  const dropAmount = publicData.priceDropAmount;
  const previousPrice =
    showPrice && dropAmount > 0 ? new Money(price.amount + dropAmount, price.currency) : null;
  const deliveryId = deliveryMessageId(pickupEnabled, shippingEnabled);

  return (
    <div className={css.header}>
      <Breadcrumb category={productCategory} itemType={productItemType} className={css.breadcrumb} />
      <div className={css.titleRow}>
        <h1 className={css.title}>{title}</h1>
        <FavoriteButton
          listingId={listing.id}
          isOwnListing={isOwnListing}
          className={css.titleFavorite}
        />
      </div>
      {showPrice ? (
        <div className={css.priceRow}>
          <span className={css.price}>{formatMoney(intl, price)}</span>
          {previousPrice ? (
            <s className={css.previousPrice}>{formatMoney(intl, previousPrice)}</s>
          ) : null}
        </div>
      ) : null}
      {locationCounty || deliveryId ? (
        <div className={css.metaRow}>
          {locationCounty ? (
            <span className={css.metaItem}>
              <IconLocation />
              {locationCounty}
            </span>
          ) : null}
          {locationCounty && deliveryId ? (
            <span className={css.metaDivider} aria-hidden="true">
              |
            </span>
          ) : null}
          {deliveryId ? (
            <span className={css.metaItem}>
              <IconBox className={css.iconBoxSmall} />
              <FormattedMessage id={deliveryId} />
            </span>
          ) : null}
        </div>
      ) : null}
      {children}
    </div>
  );
};

/**
 * Three trust signals. "Contact the seller" opens the inquiry like the bottom-bar button.
 */
export const TrustSignals = props => {
  const { onContactUser, showContact } = props;
  const contactContent = (
    <>
      <IconChat className={css.trustIcon} />
      <span className={css.trustTitle}>
        <FormattedMessage id="ListingPage.trustContactTitle" />
      </span>
      <span className={css.trustText}>
        <FormattedMessage id="ListingPage.trustContactText" />
      </span>
    </>
  );
  return (
    <ul className={css.trustRow}>
      <li className={css.trustItem}>
        <IconCard className={css.trustIcon} />
        <span className={css.trustTitle}>
          <FormattedMessage id="ListingPage.trustPaymentsTitle" />
        </span>
        <span className={css.trustText}>
          <FormattedMessage id="ListingPage.trustPaymentsText" />
        </span>
      </li>
      <li className={css.trustItem}>
        <IconBox className={css.trustIcon} />
        <span className={css.trustTitle}>
          <FormattedMessage id="ListingPage.trustDeliveryTitle" />
        </span>
        <span className={css.trustText}>
          <FormattedMessage id="ListingPage.trustDeliveryText" />
        </span>
      </li>
      <li className={css.trustItem}>
        {showContact ? (
          <button type="button" className={css.trustButton} onClick={onContactUser}>
            {contactContent}
          </button>
        ) : (
          contactContent
        )}
      </li>
    </ul>
  );
};

export const ConditionBadge = props => {
  const { publicData, listingFields } = props;
  const label = enumLabel(listingFields, 'productCondition', publicData?.productCondition);
  return label ? (
    <div className={css.condition}>
      <IconSparkle />
      <span className={css.conditionTitle}>
        <FormattedMessage id="ListingPage.conditionTitle" />
      </span>
      <span className={css.conditionValue}>{label}</span>
    </div>
  ) : null;
};

export const DescriptionCard = props => {
  const { text } = props;
  return text ? (
    <section className={css.card}>
      <h2 className={css.cardTitle}>
        <FormattedMessage id="ListingPage.descriptionTitle" />
      </h2>
      <p className={css.description}>
        {richText(text, {
          linkify: true,
          longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
          longWordClass: css.longWord,
          breakChars: '/',
        })}
      </p>
    </section>
  ) : null;
};

/**
 * Brand, category › subcategory, size, condition, gender and county (+ "Show map" when the
 * listing has a location). Same information the page showed before, in the mockup's order.
 */
export const DetailsCard = props => {
  const { publicData = {}, listingFields, hasMap, isMapOpen, onToggleMap } = props;
  const { brandName, productCategory, productItemType, size, productCondition, gender } =
    publicData;
  const county = enumLabel(listingFields, 'locationCounty', publicData.locationCounty);

  const rows = [
    brandName ? { key: 'brand', label: 'ListingPage.detailBrand', value: brandName } : null,
    productCategory
      ? {
          key: 'category',
          label: 'ListingPage.detailCategory',
          value: (
            <Breadcrumb
              category={productCategory}
              itemType={productItemType}
              className={css.detailCrumb}
            />
          ),
        }
      : null,
    size
      ? { key: 'size', label: 'ListingPage.detailSize', value: enumLabel(listingFields, 'size', size) }
      : null,
    productCondition
      ? {
          key: 'condition',
          label: 'ListingPage.detailCondition',
          value: enumLabel(listingFields, 'productCondition', productCondition),
        }
      : null,
    // 'none' = "Not available": nothing to tell the buyer, so no row.
    gender && gender !== 'none'
      ? { key: 'gender', label: 'ListingPage.detailGender', value: enumLabel(listingFields, 'gender', gender) }
      : null,
    county ? { key: 'county', label: 'ListingPage.detailCounty', value: county } : null,
  ].filter(row => row && row.value);

  if (rows.length === 0) {
    return null;
  }

  return (
    <section className={css.card}>
      <h2 className={css.cardTitle}>
        <FormattedMessage id="ListingPage.detailsTitle" />
      </h2>
      <dl className={css.details}>
        {rows.map(row => (
          <div key={row.key} className={css.detailsRow}>
            <dt className={css.detailLabel}>
              <FormattedMessage id={row.label} />
            </dt>
            <dd className={css.detailValue}>{row.value}</dd>
            {row.key === 'county' && hasMap ? (
              <button type="button" className={css.showMap} onClick={onToggleMap}>
                <IconLocation />
                <FormattedMessage id={isMapOpen ? 'ListingPage.hideMap' : 'ListingPage.showMap'} />
              </button>
            ) : null}
          </div>
        ))}
      </dl>
    </section>
  );
};

/**
 * Compact seller card: avatar, name, member since, link to the profile. Also hosts the
 * inquiry modal that every "Contact seller" button opens.
 */
export const SellerCard = props => {
  const {
    listing,
    title,
    intl,
    isInquiryModalOpen,
    onCloseInquiryModal,
    sendInquiryError,
    sendInquiryInProgress,
    onSubmitInquiry,
    onManageDisableScrolling,
  } = props;
  const author = listing.author;
  if (!author) {
    return null;
  }
  const authorDisplayName = userDisplayNameAsString(author, '');
  const createdAt = author.attributes?.createdAt;

  return (
    <section id="author" className={classNames(css.card, css.seller)}>
      <h2 className={css.cardTitle}>
        <FormattedMessage id="ListingPage.aboutSellerTitle" />
      </h2>
      <NamedLink className={css.sellerLink} name="ProfilePage" params={{ id: author.id.uuid }}>
        <AvatarMedium user={author} className={css.sellerAvatar} disableProfileLink />
        <span className={css.sellerText}>
          <span className={css.sellerName}>{authorDisplayName}</span>
          {createdAt ? (
            <span className={css.sellerSince}>
              <FormattedMessage
                id="ListingPage.memberSince"
                values={{ date: intl.formatDate(createdAt, { month: 'short', year: 'numeric' }) }}
              />
            </span>
          ) : null}
        </span>
        <IconChevron />
      </NamedLink>
      <Modal
        id="ListingPage.inquiry"
        contentClassName={css.inquiryModalContent}
        isOpen={isInquiryModalOpen}
        onClose={onCloseInquiryModal}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <InquiryForm
          className={css.inquiryForm}
          submitButtonWrapperClassName={css.inquirySubmitButtonWrapper}
          listingTitle={title}
          authorDisplayName={authorDisplayName}
          sendInquiryError={sendInquiryError}
          onSubmit={onSubmitInquiry}
          inProgress={sendInquiryInProgress}
        />
      </Modal>
    </section>
  );
};
